import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  ArrowLeft,
  Clock,
  Download,
  FileCheck2,
  Pencil,
  RefreshCw,
  Sparkles,
  Undo2,
  Wand2,
} from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  SortableHead,
  makeToggle,
  type SortState,
} from "@/components/sortable-head"
import { cn } from "@/lib/utils"

type WorklogSortCol =
  | "project"
  | "issue"
  | "summary"
  | "hours"
  | "date"
  | "assignee"

interface CycleSummary {
  milestone_id: string
  contract_id: string
  label: string | null
  amount: number
  status: string
  contract_title: string | null
  entity_name: string | null
  window_from: string | null
  window_to: string | null
  hours_consumed: number | null
  hours_included: number | null
  effective_hourly_rate: number | null
}

interface PolicyDecision {
  is_billable: boolean
  confidence: "high" | "medium" | "low"
  rationale: string
  policy_key: string
}

interface ProjectOption {
  reference: string
  title: string | null
}

interface Worklog {
  id: string
  project_reference: string | null
  project_reference_override: string | null
  issue_key: string | null
  issue_summary: string | null
  issue_summary_default: string | null
  issue_summary_override: string | null
  hours: number
  hours_default: number
  work_date: string | null
  assignee_name: string | null
  issue_type: string | null
  is_billable_default: boolean
  is_billable_override: boolean | null
  is_billable_effective: boolean
  deferred_to_next_cycle: boolean
  is_carryover: boolean
  /** window_from do ciclo que fatura a linha; null = ciclo do work_date. */
  billing_deferred_until: string | null
  policy_decision: PolicyDecision | null
  worklog_notes: string | null
}

interface Totals {
  hours_total: number
  hours_billable: number
  hours_non_billable: number
  line_count: number
}

interface DetailResponse {
  success: boolean
  data?: {
    cycle: CycleSummary
    worklogs: Worklog[]
    projects?: ProjectOption[]
    totals: Totals
    window: { from: string; to: string }
  }
  error?: string
}

interface PatchResponse {
  success: boolean
  data?: { worklog: any }
  error?: string
}

interface Props {
  shell: AppShell | null
}

function fmtHours(n: number | null | undefined): string {
  if (n == null) return "—"
  return `${n.toFixed(2)}h`
}
function fmtEur(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—"
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n)
}

/**
 * Round to the nearest quarter hour. 1.13h → 1.25h. 1.05h → 1.00h. 0h stays 0.
 */
function roundToQuarter(hours: number): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0
  return Math.round(hours * 4) / 4
}

function adaptHostWorklog(raw: any): Worklog {
  const issueSummaryDefault = raw.issue_summary_default ?? raw.task_name ?? null
  const issueSummaryOverride = raw.issue_summary_override ?? null
  return {
    id: String(raw.id),
    project_reference: raw.project_reference ?? null,
    project_reference_override: raw.project_reference_override ?? null,
    issue_key: raw.issue_key ?? raw.task_reference ?? null,
    issue_summary: raw.issue_summary ?? issueSummaryOverride ?? issueSummaryDefault,
    issue_summary_default: issueSummaryDefault,
    issue_summary_override: issueSummaryOverride,
    hours:
      raw.hours != null
        ? Number(raw.hours)
        : raw.hours_override_minutes != null
          ? Number(raw.hours_override_minutes) / 60
          : Number(raw.time_spent_minutes ?? 0) / 60,
    hours_default:
      raw.hours_default != null
        ? Number(raw.hours_default)
        : Number(raw.time_spent_minutes ?? 0) / 60,
    work_date: raw.work_date ?? null,
    assignee_name: raw.assignee_name ?? null,
    issue_type: raw.issue_type ?? raw.task_status ?? null,
    is_billable_default: raw.is_billable_default === true,
    is_billable_override:
      raw.is_billable_override === null || raw.is_billable_override === undefined
        ? null
        : raw.is_billable_override === true,
    is_billable_effective:
      raw.is_billable_effective != null
        ? raw.is_billable_effective === true
        : raw.is_billable_override != null
          ? raw.is_billable_override === true
          : raw.is_billable_default === true,
    deferred_to_next_cycle: raw.deferred_to_next_cycle === true,
    is_carryover: raw.is_carryover === true,
    billing_deferred_until: raw.billing_deferred_until ?? null,
    policy_decision: raw.policy_decision ?? null,
    worklog_notes: raw.worklog_notes ?? null,
  }
}

/**
 * `deferred_to_next_cycle` has no direction on its own: the same TRUE means
 * "adiada PARA FORA deste ciclo" on an in-window line and "transitada PARA
 * DENTRO deste ciclo" on a line the host pulled in from an earlier window
 * (`is_carryover`). Only the first kind leaves this cycle's bill — the second
 * is precisely what we are billing now (the host totals and the PDF already
 * include it), so the UI must not subtract it.
 */
function isDeferredOut(w: Worklog): boolean {
  return w.deferred_to_next_cycle && !w.is_carryover
}

/**
 * One billable line per Jira issue: the cycle's worklogs are grouped by
 * issue_key and their effective hours summed. Editing (billable / defer /
 * summary / project) happens at the issue level and applies to every
 * worklog in the group. Hours are read-only here — the "Ajustar horas"
 * batch still rounds each underlying worklog and the sum re-derives.
 */
interface IssueGroup {
  key: string
  issue_key: string | null
  worklogs: Worklog[]
  project_reference: string | null
  project_reference_override: string | null
  issue_summary: string | null
  issue_summary_default: string | null
  issue_summary_override: string | null
  hours: number
  work_date_min: string | null
  work_date_max: string | null
  assignees: string[]
  is_billable_effective: boolean
  is_billable_mixed: boolean
  deferred: boolean
  deferred_mixed: boolean
  is_carryover: boolean
  policy: PolicyDecision | null
  policy_evaluated: number
}

function buildIssueGroups(worklogs: Worklog[]): IssueGroup[] {
  const map = new Map<string, Worklog[]>()
  for (const w of worklogs) {
    const key = w.issue_key ?? `__wl_${w.id}`
    const arr = map.get(key)
    if (arr) arr.push(w)
    else map.set(key, [w])
  }

  const groups: IssueGroup[] = []
  for (const [key, wls] of map) {
    const overrideProject =
      wls.find((w) => w.project_reference_override)?.project_reference_override ?? null
    const projectReference =
      wls.find((w) => w.project_reference)?.project_reference ?? null
    const overrideSummary =
      wls.find((w) => w.issue_summary_override)?.issue_summary_override ?? null
    const summaryDefault =
      wls.find((w) => w.issue_summary_default)?.issue_summary_default ?? null
    const summary = overrideSummary ?? summaryDefault ?? wls[0].issue_summary ?? null

    const dates = wls
      .map((w) => w.work_date)
      .filter((d): d is string => !!d)
      .sort()
    const assignees = Array.from(
      new Set(wls.map((w) => w.assignee_name).filter((a): a is string => !!a)),
    )

    const billable = wls.map((w) => w.is_billable_effective)
    const deferred = wls.map(isDeferredOut)

    // Representative KOORTEX decision: only when every worklog has been
    // evaluated and they all agree on is_billable. Confidence = the lowest
    // among them (most conservative). Otherwise null (shows "—"/partial).
    const decided = wls.filter((w) => w.policy_decision)
    let policy: PolicyDecision | null = null
    if (decided.length === wls.length && decided.length > 0) {
      const allBillableSame = decided.every(
        (w) => w.policy_decision!.is_billable === decided[0].policy_decision!.is_billable,
      )
      if (allBillableSame) {
        const order = { low: 0, medium: 1, high: 2 } as const
        const lowest = decided.reduce((acc, w) =>
          order[w.policy_decision!.confidence] < order[acc.policy_decision!.confidence]
            ? w
            : acc,
        )
        policy = lowest.policy_decision
      }
    }

    groups.push({
      key,
      issue_key: wls[0].issue_key,
      worklogs: wls,
      project_reference: projectReference,
      project_reference_override: overrideProject,
      issue_summary: summary,
      issue_summary_default: summaryDefault,
      issue_summary_override: overrideSummary,
      hours: wls.reduce((s, w) => s + w.hours, 0),
      work_date_min: dates[0] ?? null,
      work_date_max: dates[dates.length - 1] ?? null,
      assignees,
      is_billable_effective: billable.every(Boolean),
      is_billable_mixed: billable.some(Boolean) && !billable.every(Boolean),
      deferred: deferred.every(Boolean),
      deferred_mixed: deferred.some(Boolean) && !deferred.every(Boolean),
      is_carryover: wls.some((w) => w.is_carryover),
      policy,
      policy_evaluated: decided.length,
    })
  }
  return groups
}

export function CycleDetail({ shell }: Props) {
  const { contractId, milestoneId } = useParams<{
    contractId: string
    milestoneId: string
  }>()
  const navigate = useNavigate()
  const [cycle, setCycle] = useState<CycleSummary | null>(null)
  // Janela [from, to) do ciclo em revisão. O host precisa dela para traduzir
  // o toggle de adiar em `billing_deferred_until` (ver lib/contracts/deferral
  // no host) — sem ela cai no boolean legacy.
  const [cycleWindow, setCycleWindow] = useState<{ from: string; to: string } | null>(null)
  const [worklogs, setWorklogs] = useState<Worklog[]>([])
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [totals, setTotals] = useState<Totals | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [evaluateProgress, setEvaluateProgress] = useState<{ done: number; total: number } | null>(null)
  const [invoiceModal, setInvoiceModal] = useState(false)
  const [editing, setEditing] = useState<IssueGroup | null>(null)
  const [sort, setSort] = useState<SortState<WorklogSortCol>>({
    col: "date",
    dir: "asc",
  })
  const toggleSort = makeToggle(setSort)

  async function loadDetail() {
    if (!shell || !contractId || !milestoneId) return
    setLoading(true)
    setError(null)
    try {
      const res = await shell.client.post<DetailResponse>("actions/get_cycle_detail", {
        params: { contract_id: contractId, milestone_id: milestoneId },
      })
      if (!res.success || !res.data) {
        setError(res.error ?? "Falha ao carregar o ciclo")
        return
      }
      setCycle(res.data.cycle)
      setCycleWindow(res.data.window ?? null)
      setWorklogs(res.data.worklogs.map(adaptHostWorklog))
      setProjects(res.data.projects ?? [])
      setTotals(res.data.totals)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, contractId, milestoneId])

  const sortedGroups = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1
    const out = buildIssueGroups(worklogs)
    out.sort((a, b) => {
      switch (sort.col) {
        case "project":
          return (
            (a.project_reference_override ?? a.project_reference ?? "").localeCompare(
              b.project_reference_override ?? b.project_reference ?? "",
            ) * dir
          )
        case "issue":
          return (a.issue_key ?? "").localeCompare(b.issue_key ?? "") * dir
        case "summary":
          return (a.issue_summary ?? "").localeCompare(b.issue_summary ?? "") * dir
        case "hours":
          return (a.hours - b.hours) * dir
        case "date":
          return (a.work_date_min ?? "").localeCompare(b.work_date_min ?? "") * dir
        case "assignee":
          return (a.assignees[0] ?? "").localeCompare(b.assignees[0] ?? "") * dir
      }
    })
    return out
  }, [worklogs, sort])

  function recomputeTotals(rows: Worklog[]): Totals {
    let total = 0
    let billable = 0
    for (const w of rows) {
      // Lines deferred OUT of this cycle don't count in its totals — they'll
      // show up in next cycle's review. Carried-in lines (is_carryover) do
      // count: this is the cycle that bills them.
      if (isDeferredOut(w)) continue
      total += w.hours
      if (w.is_billable_effective) billable += w.hours
    }
    return {
      hours_total: total,
      hours_billable: billable,
      hours_non_billable: total - billable,
      line_count: rows.length,
    }
  }

  async function patchWorklog(worklogId: string, patch: Record<string, unknown>) {
    if (!shell || !contractId) return null
    try {
      const res = await shell.client.post<PatchResponse>("actions/patch_worklog", {
        params: {
          contract_id: contractId,
          worklog_id: worklogId,
          // Sempre enviada: o host só a usa para o toggle de adiar, e assim
          // nenhum caller se pode esquecer dela.
          ...(cycleWindow
            ? { cycle_window_from: cycleWindow.from, cycle_window_to: cycleWindow.to }
            : {}),
          ...patch,
        },
      })
      if (!res.success || !res.data) {
        shell.bridge.toast({ variant: "error", description: res.error ?? "Patch falhou" })
        return null
      }
      const updated = adaptHostWorklog(res.data.worklog)
      setWorklogs((prev) => {
        const next = prev.map((w) => (w.id === worklogId ? updated : w))
        setTotals(recomputeTotals(next))
        return next
      })
      return updated
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : String(err),
      })
      return null
    }
  }

  // Issue-level edit: apply the same patch to every worklog of the issue.
  // Used by the billable/defer toggles and the edit dialog (summary +
  // project). Runs sequentially so each patchWorklog refreshes state.
  async function patchIssueGroup(group: IssueGroup, patch: Record<string, unknown>) {
    for (const w of group.worklogs) {
      await patchWorklog(w.id, patch)
    }
  }

  async function adjustHoursToQuarter() {
    if (!shell || !contractId) return
    const toAdjust = worklogs.filter((w) => {
      if (isDeferredOut(w)) return false
      if (w.hours <= 0) return false
      const rounded = roundToQuarter(w.hours)
      return Math.abs(rounded - w.hours) > 0.001
    })
    if (toAdjust.length === 0) {
      shell.bridge.toast({
        variant: "info",
        description: "Todas as linhas já estão em múltiplos de 0.25h.",
      })
      return
    }
    setBusyAction("adjust")
    let ok = 0
    let failed = 0
    for (const w of toAdjust) {
      const minutes = Math.round(roundToQuarter(w.hours) * 60)
      const updated = await patchWorklog(w.id, { hours_override_minutes: minutes })
      if (updated) ok++
      else failed++
    }
    setBusyAction(null)
    shell.bridge.toast({
      variant: failed === 0 ? "success" : "error",
      description:
        failed === 0
          ? `${ok} linha${ok === 1 ? "" : "s"} ajustada${ok === 1 ? "" : "s"} a múltiplos de 0.25h.`
          : `${ok} ajustadas, ${failed} falhadas.`,
    })
  }

  async function evaluatePolicy() {
    if (!shell || !contractId) return

    // Find lines that haven't been evaluated yet. We chunk client-side so
    // a 200-worklog cycle doesn't hit the 180s manifest timeout — each
    // chunk is its own request with its own timeout budget. Already-
    // evaluated lines (have policy_decision) stay untouched.
    const candidates = worklogs
      .filter((w) => !isDeferredOut(w) && w.policy_decision == null)
      .map((w) => w.id)

    if (candidates.length === 0) {
      shell.bridge.toast({
        variant: "info",
        description: "Todas as linhas já têm avaliação KOORTEX. Limpa policy_decision na DB para re-avaliar.",
      })
      return
    }

    const CHUNK_SIZE = 25
    const chunks: string[][] = []
    for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
      chunks.push(candidates.slice(i, i + CHUNK_SIZE))
    }

    setBusyAction("evaluate")
    setEvaluateProgress({ done: 0, total: candidates.length })
    let evaluated = 0
    const errors: string[] = []
    try {
      for (let idx = 0; idx < chunks.length; idx++) {
        const chunk = chunks[idx]
        try {
          const res = await shell.client.post<any>("actions/evaluate_policy", {
            params: {
              contract_id: contractId,
              worklog_ids: chunk,
              only_unevaluated: false,
            },
          })
          if (!res.success) {
            errors.push(`chunk ${idx + 1}: ${res.error ?? "unknown"}`)
          } else {
            evaluated += Number(res.data?.evaluated ?? 0)
          }
        } catch (err) {
          errors.push(`chunk ${idx + 1}: ${err instanceof Error ? err.message : String(err)}`)
        }
        setEvaluateProgress({
          done: Math.min(candidates.length, (idx + 1) * CHUNK_SIZE),
          total: candidates.length,
        })
      }
      if (errors.length > 0) {
        shell.bridge.toast({
          variant: "error",
          description: `${evaluated} avaliadas, ${errors.length} chunk(s) falharam`,
        })
      } else {
        shell.bridge.toast({
          variant: "success",
          description: `Avaliadas ${evaluated} linhas`,
        })
      }
      await loadDetail()
    } finally {
      setBusyAction(null)
      setEvaluateProgress(null)
    }
  }

  async function downloadPdf() {
    if (!shell || !contractId || !milestoneId) return
    setBusyAction("pdf")
    try {
      const res = await shell.client.post<any>("actions/download_pdf", {
        params: { contract_id: contractId, milestone_id: milestoneId },
      })
      if (!res.success || !res.data?.content_base64) {
        shell.bridge.toast({ variant: "error", description: res.error ?? "PDF falhou" })
        return
      }
      const bin = atob(res.data.content_base64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      const blob = new Blob([bytes], { type: res.data.content_type ?? "application/pdf" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = res.data.filename ?? "sla.pdf"
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setBusyAction(null)
    }
  }

  async function submitInvoice() {
    if (!shell || !contractId || !milestoneId) return
    setInvoiceModal(false)
    setBusyAction("invoice")
    try {
      const res = await shell.client.post<any>("actions/generate_invoice", {
        params: { contract_id: contractId, milestone_id: milestoneId },
      })
      if (!res.success) {
        shell.bridge.toast({ variant: "error", description: res.error ?? "Fatura falhou" })
        return
      }
      const url = res.data?.edit_url
      shell.bridge.toast({
        variant: "success",
        description: url ? "Fatura draft criada — abrindo Moloni" : "Fatura draft criada",
      })
      if (url) window.open(url, "_blank", "noopener,noreferrer")
      await loadDetail()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setBusyAction(null)
    }
  }

  if (!shell) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Standalone dev mode.</div>
    )
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 h-7 gap-1 text-muted-foreground"
            // Rota explícita, não "/": o catch-all leva ao ecrã de entrada do
            // token (o separador que o menu do CRM abriu), que raramente é o
            // billing.
            onClick={() => navigate("/billing/invoicing")}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Ciclos
          </Button>
          <h1 className="text-xl font-semibold leading-tight">
            {cycle?.contract_title ?? "Ciclo"}
            {cycle?.entity_name && (
              <span className="ml-2 text-base font-normal text-muted-foreground">
                · {cycle.entity_name}
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {cycle?.window_from} → {cycle?.window_to}
            {cycle?.label ? ` · ${cycle.label}` : null}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadDetail} disabled={loading} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? "A actualizar…" : "Actualizar"}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Horas totais" value={fmtHours(totals?.hours_total)} />
        <KpiCard
          label="Billable"
          value={fmtHours(totals?.hours_billable)}
          tone="positive"
        />
        <KpiCard
          label="Horas perdoadas"
          value={fmtHours(totals?.hours_non_billable)}
          subline={
            totals?.hours_non_billable && cycle?.effective_hourly_rate
              ? `≈ ${fmtEur(totals.hours_non_billable * cycle.effective_hourly_rate)} não-cobrados`
              : undefined
          }
          tone="muted"
        />
        <KpiCard label="Valor previsto" value={fmtEur(cycle?.amount)} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={evaluatePolicy}
              disabled={busyAction != null}
              className="gap-2"
            >
              <Sparkles className="h-4 w-4" />
              {busyAction === "evaluate"
                ? evaluateProgress
                  ? `A avaliar… ${evaluateProgress.done}/${evaluateProgress.total}`
                  : "A avaliar…"
                : "Avaliar com KOORTEX"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={adjustHoursToQuarter}
              disabled={busyAction != null}
              className="gap-2"
            >
              <Wand2 className="h-4 w-4" />
              {busyAction === "adjust" ? "A ajustar…" : "Ajustar horas (0.25h)"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              disabled={busyAction != null}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {busyAction === "pdf" ? "A gerar…" : "Descarregar PDF"}
            </Button>
          </div>
          <Button
            size="sm"
            onClick={() => setInvoiceModal(true)}
            disabled={busyAction != null || (totals?.hours_billable ?? 0) === 0}
            className="gap-2"
          >
            <FileCheck2 className="h-4 w-4" />
            {busyAction === "invoice" ? "A submeter…" : "Submeter fatura"}
          </Button>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Projeto" col="project" sort={sort} onClick={toggleSort} />
                  <SortableHead label="Issue" col="issue" sort={sort} onClick={toggleSort} />
                  <SortableHead
                    label="Sumário"
                    col="summary"
                    sort={sort}
                    onClick={toggleSort}
                    className="min-w-[280px]"
                  />
                  <SortableHead label="Horas" col="hours" sort={sort} onClick={toggleSort} align="right" />
                  <SortableHead label="Data" col="date" sort={sort} onClick={toggleSort} />
                  <SortableHead label="Recurso" col="assignee" sort={sort} onClick={toggleSort} />
                  <TableHead>KOORTEX</TableHead>
                  <TableHead className="text-center">Billable</TableHead>
                  <TableHead className="text-center">Adiar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedGroups.map((g) => (
                  <IssueGroupRow
                    key={g.key}
                    group={g}
                    onPatch={(patch) => patchIssueGroup(g, patch)}
                    onEditSummary={() => setEditing(g)}
                  />
                ))}
                {sortedGroups.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center text-sm text-muted-foreground">
                      Sem worklogs nesta janela.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Invoice submit modal */}
      <Dialog open={invoiceModal} onOpenChange={setInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submeter fatura para Moloni</DialogTitle>
            <DialogDescription>
              Vai criar uma fatura <strong>draft</strong> no Moloni. Podes ainda editar e
              enviar manualmente depois — esta acção não envia ao cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 px-3 py-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Horas billable</span>
              <span className="font-medium tabular-nums">{fmtHours(totals?.hours_billable)}</span>
            </div>
            <div className="mt-1 flex justify-between">
              <span className="text-muted-foreground">Taxa horária</span>
              <span className="font-medium tabular-nums">{fmtEur(cycle?.effective_hourly_rate)}</span>
            </div>
            <div className="mt-1 flex justify-between border-t pt-1">
              <span className="text-muted-foreground">Total estimado</span>
              <span className="font-semibold tabular-nums">
                {fmtEur(
                  (totals?.hours_billable ?? 0) * (cycle?.effective_hourly_rate ?? 0),
                )}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceModal(false)}>
              Cancelar
            </Button>
            <Button onClick={submitInvoice}>Submeter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit line modal (summary + project reclassification) — issue level */}
      <EditSummaryDialog
        group={editing}
        projects={projects}
        onClose={() => setEditing(null)}
        onSave={async (group, patch) => {
          // Hours are per-worklog (each line keeps its own value), so they
          // are patched individually.
          for (const h of patch.hours) {
            await patchWorklog(h.id, { hours_override_minutes: h.minutes })
          }

          const body: Record<string, unknown> = {}

          // Summary — empty string clears the override → Jira task name.
          const trimmed = patch.summary.trim()
          const nextSummary = trimmed.length > 0 ? trimmed : null
          if (nextSummary !== (group.issue_summary_override ?? null)) {
            body.issue_summary_override = nextSummary
          }

          // Project — selecting the synced original clears the override.
          const nextProject =
            patch.projectOverride && patch.projectOverride !== group.project_reference
              ? patch.projectOverride
              : null
          if (nextProject !== (group.project_reference_override ?? null)) {
            body.project_reference_override = nextProject
          }

          // Summary + project apply to every worklog of the issue.
          if (Object.keys(body).length > 0) {
            await patchIssueGroup(group, body)
          }
          setEditing(null)
        }}
      />
    </div>
  )
}

function KpiCard({
  label,
  value,
  subline,
  tone,
}: {
  label: string
  value: string
  subline?: string
  tone?: "positive" | "muted"
}) {
  const tint =
    tone === "positive"
      ? "bg-success/10 border-success/30"
      : tone === "muted"
        ? "bg-muted/40"
        : "bg-card"
  return (
    <Card className={`${tint} shadow-none`}>
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        {subline && (
          <div className="mt-1 text-xs text-muted-foreground">{subline}</div>
        )}
      </CardContent>
    </Card>
  )
}

function fmtDateRange(min: string | null, max: string | null): string {
  if (!min && !max) return "—"
  if (!min) return max ?? "—"
  if (!max || min === max) return min
  return `${min} – ${max}`
}

function IssueGroupRow({
  group,
  onPatch,
  onEditSummary,
}: {
  group: IssueGroup
  onPatch: (patch: Record<string, unknown>) => void
  onEditSummary: () => void
}) {
  const effectiveProject = group.project_reference_override ?? group.project_reference
  const isReclassified =
    group.project_reference_override != null &&
    group.project_reference_override !== group.project_reference
  const count = group.worklogs.length

  const rowTint = group.deferred
    ? "bg-amber-50/60 dark:bg-amber-950/20"
    : !group.is_billable_effective
      ? "bg-muted/30"
      : ""

  const textOpacity = group.deferred || !group.is_billable_effective ? "opacity-70" : ""

  const assigneeLabel =
    group.assignees.length === 0
      ? "—"
      : group.assignees.length === 1
        ? group.assignees[0]
        : `Vários (${group.assignees.length})`

  return (
    <TableRow className={cn(rowTint, textOpacity)}>
      <TableCell className="text-sm">
        <div className="flex items-center gap-1.5">
          <span>{effectiveProject ?? "—"}</span>
          {isReclassified && (
            <Badge
              variant="outline"
              className="h-5 shrink-0 px-1.5 text-[10px]"
              title={`Original: ${group.project_reference ?? "—"}`}
            >
              reclass.
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <code className="font-mono text-xs">{group.issue_key ?? "—"}</code>
      </TableCell>
      <TableCell className="max-w-[360px] text-sm">
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={onEditSummary}
            className="group flex flex-1 items-start gap-1 text-left transition-colors hover:text-foreground"
            title="Editar linha (sumário + projeto)"
          >
            <span className="line-clamp-2">{group.issue_summary ?? "—"}</span>
            <Pencil className="mt-0.5 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
          </button>
          {count > 1 && (
            <Badge
              variant="secondary"
              className="h-5 shrink-0 px-1.5 text-[10px] tabular-nums"
              title={`${count} worklogs somados nesta issue`}
            >
              {count}×
            </Badge>
          )}
          {group.issue_summary_override && (
            <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px]">
              edited
            </Badge>
          )}
          {group.is_carryover && (
            <Badge
              variant="outline"
              className="h-5 shrink-0 px-1.5 text-[10px]"
              title="Linha adiada num ciclo anterior — é faturada neste ciclo"
            >
              carry
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right text-sm font-medium tabular-nums">
        {group.hours.toFixed(2)}h
      </TableCell>
      <TableCell className="text-sm text-muted-foreground tabular-nums">
        {fmtDateRange(group.work_date_min, group.work_date_max)}
      </TableCell>
      <TableCell className="text-sm" title={group.assignees.join(", ")}>
        {assigneeLabel}
      </TableCell>
      <TableCell>
        {group.policy ? (
          <Badge
            variant={
              group.policy.confidence === "high"
                ? "default"
                : group.policy.confidence === "medium"
                  ? "secondary"
                  : "outline"
            }
            title={group.policy.rationale}
            className="cursor-help gap-1 capitalize"
          >
            {group.policy.is_billable ? "✓" : "✗"} {group.policy.confidence}
          </Badge>
        ) : group.policy_evaluated > 0 ? (
          <Badge variant="outline" className="text-[10px]" title="Avaliação parcial ou divergente entre worklogs">
            parcial
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-center">
        <Switch
          checked={group.is_billable_effective}
          onCheckedChange={(v) => onPatch({ is_billable_override: v })}
          disabled={group.deferred}
          title={group.is_billable_mixed ? "Misto — aplica a todos os worklogs da issue" : undefined}
        />
      </TableCell>
      <TableCell className="text-center">
        <Button
          variant={group.deferred ? "default" : "ghost"}
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => onPatch({ deferred_to_next_cycle: !group.deferred })}
          title={
            group.deferred
              ? "Recuperar para este ciclo"
              : group.is_carryover
                ? "Transitada de um ciclo anterior — adiar outra vez empurra-a para o próximo"
                : "Adiar para o próximo ciclo"
          }
        >
          {group.deferred ? (
            <>
              <Undo2 className="h-3 w-3" /> Recuperar
            </>
          ) : (
            <>
              <Clock className="h-3 w-3" /> Adiar
            </>
          )}
        </Button>
      </TableCell>
    </TableRow>
  )
}

function EditSummaryDialog({
  group,
  projects,
  onClose,
  onSave,
}: {
  group: IssueGroup | null
  projects: ProjectOption[]
  onClose: () => void
  onSave: (
    group: IssueGroup,
    patch: {
      summary: string
      projectOverride: string | null
      hours: Array<{ id: string; minutes: number }>
    },
  ) => Promise<void>
}) {
  const [value, setValue] = useState("")
  const [projectValue, setProjectValue] = useState("")
  const [hoursInputs, setHoursInputs] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const originalProject = group?.project_reference ?? null

  useEffect(() => {
    if (group) {
      setValue(group.issue_summary ?? "")
      setProjectValue(
        group.project_reference_override ?? group.project_reference ?? "",
      )
      setHoursInputs(
        Object.fromEntries(group.worklogs.map((w) => [w.id, w.hours.toFixed(2)])),
      )
    }
  }, [group])

  const liveTotalHours = group
    ? group.worklogs.reduce((s, w) => {
        const parsed = parseFloat((hoursInputs[w.id] ?? "").replace(",", "."))
        return s + (Number.isFinite(parsed) && parsed >= 0 ? parsed : 0)
      }, 0)
    : 0

  // Picklist = synced original (always present + labelled) + candidate
  // projects of the contract's entity. Deduped by reference.
  const projectOptions = useMemo(() => {
    const map = new Map<string, string>()
    if (originalProject) map.set(originalProject, originalProject)
    for (const p of projects) {
      map.set(p.reference, p.title ? `${p.reference} — ${p.title}` : p.reference)
    }
    return Array.from(map.entries()).map(([reference, label]) => ({
      reference,
      label: reference === originalProject ? `${label} (original)` : label,
    }))
  }, [projects, originalProject])

  if (!group) return null

  const isOverridden = group.issue_summary_override != null
  const isProjectOverridden =
    group.project_reference_override != null &&
    group.project_reference_override !== group.project_reference
  const defaultLabel = group.issue_summary_default ?? ""

  // Per-worklog hours edits: collect only the ones whose input differs from
  // the current effective hours. Each maps to a hours_override_minutes patch.
  function collectHoursChanges(): Array<{ id: string; minutes: number }> {
    if (!group) return []
    const out: Array<{ id: string; minutes: number }> = []
    for (const w of group.worklogs) {
      const raw = hoursInputs[w.id]
      if (raw == null) continue
      const parsed = parseFloat(raw.replace(",", "."))
      if (!Number.isFinite(parsed) || parsed < 0) continue
      const minutes = Math.round(parsed * 60)
      if (minutes !== Math.round(w.hours * 60)) out.push({ id: w.id, minutes })
    }
    return out
  }

  async function save() {
    if (!group) return
    setSaving(true)
    try {
      await onSave(group, {
        summary: value,
        projectOverride: projectValue || null,
        hours: collectHoursChanges(),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar linha</DialogTitle>
          <DialogDescription>
            Ajusta as horas, reclassifica a issue para outro projeto e/ou edita
            o sumário que aparece no PDF. Sumário e projeto aplicam-se a todos
            os {group.worklogs.length} worklog(s) da issue; as horas são por
            worklog.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Issue</Label>
            <p className="text-sm">
              <code className="font-mono text-xs">{group.issue_key ?? "—"}</code>
            </p>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">
              Horas {group.worklogs.length > 1 && `(${group.worklogs.length} worklogs)`}
            </Label>
            {/* Own scroll box: an issue with 20+ worklogs would otherwise push
                the projeto/sumário fields out of the dialog. */}
            <div className="mt-1 max-h-[34dvh] space-y-1.5 overflow-y-auto pr-1">
              {group.worklogs.map((w) => (
                <div key={w.id} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-xs text-muted-foreground">
                    {w.work_date ?? "—"}
                    {w.assignee_name ? ` · ${w.assignee_name}` : ""}
                  </span>
                  <Input
                    value={hoursInputs[w.id] ?? ""}
                    onChange={(e) =>
                      setHoursInputs((prev) => ({ ...prev, [w.id]: e.target.value }))
                    }
                    inputMode="decimal"
                    className="h-8 w-20 text-right tabular-nums"
                  />
                </div>
              ))}
            </div>
            {group.worklogs.length > 1 && (
              <p className="mt-1 text-right text-[10px] text-muted-foreground tabular-nums">
                Total: {liveTotalHours.toFixed(2)}h
              </p>
            )}
          </div>

          {projectOptions.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground">
                Projeto {isProjectOverridden && "(reclassificado)"}
              </Label>
              <Select
                value={projectValue || undefined}
                onValueChange={setProjectValue}
              >
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Escolhe o projeto…" />
                </SelectTrigger>
                <SelectContent>
                  {projectOptions.map((opt) => (
                    <SelectItem key={opt.reference} value={opt.reference}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Escolher o projeto original limpa a reclassificação. Não altera os
                dados sincronizados do Jira.
              </p>
            </div>
          )}

          {isOverridden && defaultLabel && (
            <div>
              <Label className="text-xs text-muted-foreground">
                Original do Jira
              </Label>
              <p className="text-sm text-muted-foreground">{defaultLabel}</p>
            </div>
          )}
          <div>
            <Label htmlFor="edit-summary" className="text-xs text-muted-foreground">
              Sumário {isOverridden && "(editado)"}
            </Label>
            <textarea
              id="edit-summary"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={3}
              maxLength={500}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Escreve o sumário que vai para o PDF…"
            />
            <p className="mt-1 text-right text-[10px] text-muted-foreground">
              {value.length}/500
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-between sm:justify-between">
          {isOverridden || isProjectOverridden ? (
            <Button
              variant="ghost"
              onClick={async () => {
                setValue("")
                setProjectValue(originalProject ?? "")
                setSaving(true)
                try {
                  await onSave(group, { summary: "", projectOverride: null, hours: [] })
                } finally {
                  setSaving(false)
                }
              }}
              disabled={saving}
            >
              Limpar overrides
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
